import React, { useEffect, useState } from "react";
import AddButtonSection from '../../components/common/AddButtonSection';
import {
  generateColumnMetadata,
  renderInputHeader,
  orderObj,
  addLookUpOption,
  fetchOptions,
  convertToSearchField,
} from "../../utils/utils";
import { useForm } from "react-hook-form";
import DataGrid from '../../components/common/dataGrid/dataGrid';
import { commonPostApi } from "../../utils/apiService";
import { API_ENDPOINTS } from "../../utils/apiEndpoints";
import ButtonWithSearch from '../../components/common/buttonWithSearch';
import { MedicalPrescriptionsColumns, nonRequireMedicalListField } from "../../utils/serviceData";
import { Button } from "primereact/button";
import { showToast } from '../../components/common/toast';
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../constants/toast";
import { SORT_ORDER } from "../../constants/sortOrder";
import PrescriptionDeleteConfirmation from "./PrescriptionDeleteConfirmation";

export default function MedicalPrescription({ patientVisitId, initialId }) {
  const {
    control,
    formState: { errors, dirtyFields },
    watch,
    setValue,
    handleSubmit,
    reset,
    register,
  } = useForm();

  const headers = {
    "Content-Type": "application/json",
  };

  const searchFields = [
    "medicine_name",
    "dosage",
    "type",
    "frequency",
    "route",
    "period",
  ]
  const [listLoading, setListLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const initialVal = convertToSearchField(searchFields, "");
  const [medicineId, setMedicineId] = useState("");
  const [navigator, setNavigator] = useState({
    page: 1,
    first: 0,
    rows: 10,
    totalRecords: 1,
    sortBy: "medicine_name",
    sortDir: SORT_ORDER.ASC,
    searchCriteria: {
      ...initialVal
    },
  });

  const [productList, setProductList] = useState([]);
  const [listValues, setListValues] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [edit, setEdit] = useState(false);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [dosageOptions, setDosageOptions] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [routeOptions, setRouteOptions] = useState([]);
  const [periodOptions, setPeriodOptions] = useState([]);
  const [selectedPrescription, setSelectedPrescription] = useState([]);
  const [visibility, setVisibility] = useState({
    deletePrescription: false,
  });
  const medicalFields = [
    "medicine_name",
    "dosage",
    "type",
    "frequency",
    "route",
    "period",
  ];

  const resetAllFields = () => {
    reset({
      medicine_name: "",
      dosage: "",
      type: "",
      frequency: "",
      route: "",
      period: "",
    });
  };

  const itemRenderer = (item, props) => (
    <div className="p-menuitem-content">
      {item.label === "Edit" && (
        <Button
          className="card-button-edit"
          label="Edit"
          onClick={() => {
            const displayData = props?.props?.itemProp;
            const formData = originalData.find(item =>
              item.medical_prescription_id === displayData?.medical_prescription_id
            );

            setMedicineId(formData?.medical_prescription_id || null);
            medicalFields.forEach((field) => {
              let value = "";
              if (formData && formData[field]) {
                if (typeof formData[field] === 'object') {
                  if (formData[field].value && formData[field].value.trim() !== '') {
                    value = formData[field].value;
                  } else if (formData[field].name && formData[field].name.trim() !== '') {
                    value = formData[field].name;
                  } else {
                    value = "";
                  }
                } else {
                  // Handle old string format (fallback)
                  value = formData[field];
                }
              }
              setValue(field, value || "");
            });
            setEdit(true);
          }}
        />
      )}
      {item.label === "Remove" && (
        <Button
          className="card-button-delete"
          label="Delete"
          onClick={() => {
            setSelectedPrescription(props?.props?.itemProp?.medical_prescription_id)
            setVisibility(prev => ({ ...prev, deletePrescription: true }));
          }}
        />
      )}
    </div>
  );

  const handleDelete = async (id) => {
    if (!id) return;

    if (window.confirm(COMMON_MSG.CONFIRM.DELETE_PRESCRIPTION)) {
      try {
        await commonPostApi(
          API_ENDPOINTS.DELETE_MEDICINE,
          { medical_prescription_id: id },
          {
            headers,
          }
        );
        showToast(TOAST_TYPE.SUCCESS, FEATURE_MSG.EXAMINATION.PRESCRIPTION_DELETED);
        init(navigator);
      } catch (err) {
        showToast(TOAST_TYPE.ERROR, FEATURE_MSG.EXAMINATION.PRESCRIPTION_DELETE_FAILED);
      }
    }
  };

  const formData = [
    {
      parentType: "wrap",
      wrap: [
        {
          type: "input-group",
          parentType: "wrap-with-flex",
          customClass: "py-3",
          formRender: [
            {
              type: "singleselect-dropdown",
              name: "medicine_name",
              label: "Name",
              fieldLabel: "medicine_name",
              required: true,
              placeholder: "Select",
              inputParentClass: "w-full md:pr-3 pr-2 div_pb-0",
              options: medicineOptions,
              rules: {
                required: "Medicine name is required"
              },
              nestedInput: { ...register("_medicine_name") },
              nestedOnClick: () => {
                if (dirtyFields?._medicine_name) {
                  addLookUpOption(
                    {
                      lookup_type_name: "medicine_name",
                      name: watch("_medicine_name"),
                    },
                    () => {
                      fetchOptions(
                        "medicine_name",
                        setMedicineOptions,
                        showToast
                      );
                    },
                    showToast
                  );
                  setValue("_medicine_name", "");
                }
              },
            },
          ],
        },
        {
          type: "input-group",
          parentType: "wrap-with-flex",
          customClass: "py-3",
          formRender: [
            {
              type: "singleselect-dropdown",
              name: "dosage",
              label: "Dosage",
              fieldLabel: "dosage",
              required: true,
              placeholder: "Type here",
              inputParentClass: "w-full md:pr-3 pr-2 div_pb-0",
              options: dosageOptions,
              rules: {
                required: "Dosage is required"
              },
              nestedInput: { ...register("_dosage") },
              nestedOnClick: () => {
                if (dirtyFields?._dosage) {
                  addLookUpOption(
                    {
                      lookup_type_name: "medicine_dosage",
                      name: watch("_dosage"),
                    },
                    () => {
                      fetchOptions("medicine_dosage", setDosageOptions, showToast);
                    },
                    showToast
                  );
                  setValue("_dosage", "");
                }
              },
            },
          ],
        },
        {
          type: "input-group",
          parentType: "wrap-with-flex",
          customClass: "py-3",
          formRender: [
            {
              type: "singleselect-dropdown",
              name: "type",
              label: "Type",
              fieldLabel: "type",
              placeholder: "Type here",
              inputParentClass: "w-full md:pr-3 pr-2 div_pb-0",
              options: typeOptions,
              nestedInput: { ...register("_type") },
              nestedOnClick: () => {
                if (dirtyFields?._type) {
                  addLookUpOption(
                    {
                      lookup_type_name: "medicine_type",
                      name: watch("_type"),
                    },
                    () => {
                      fetchOptions("medicine_type", setTypeOptions, showToast);
                    },
                    showToast
                  );
                  setValue("_type", "");
                }
              },
            },
          ],
        },
      ],
    },
    {
      parentType: "wrap",
      wrap: [
        {
          type: "input-group",
          parentType: "wrap-with-flex",
          customClass: "py-3",
          formRender: [
            {
              type: "singleselect-dropdown",
              name: "frequency",
              label: "Frequency",
              fieldLabel: "frequency",
              placeholder: "Select",
              inputParentClass: "w-full md:pr-3 pr-2 div_pb-0",
              options: frequencyOptions,

              nestedInput: { ...register("_frequency") },
              nestedOnClick: () => {
                if (dirtyFields?._frequency) {
                  addLookUpOption(
                    {
                      lookup_type_name: "medicine_frequency",
                      name: watch("_frequency"),
                    },
                    () => {
                      fetchOptions("medicine_frequency", setFrequencyOptions, showToast);
                    },
                    showToast
                  );
                  setValue("_frequency", "");
                }
              },
            },
          ],
        },
        {
          type: "input-group",
          parentType: "wrap-with-flex",
          customClass: "py-3",
          formRender: [
            {
              type: "singleselect-dropdown",
              name: "route",
              label: "Route",
              fieldLabel: "route",
              placeholder: "Select",
              inputParentClass: "w-full md:pr-3 pr-2 div_pb-0",
              options: routeOptions,

              nestedInput: { ...register("_route") },
              nestedOnClick: () => {
                if (dirtyFields?._route) {
                  addLookUpOption(
                    {
                      lookup_type_name: "medicine_route",
                      name: watch("_route"),
                    },
                    () => {
                      fetchOptions("medicine_route", setRouteOptions, showToast);
                    },
                    showToast
                  );
                  setValue("_route", "");
                }
              },
            },
          ],
        },
        {
          type: "input-group",
          parentType: "wrap-with-flex",
          customClass: "py-3",
          formRender: [
            {
              type: "singleselect-dropdown",
              name: "period",
              label: "Period",
              fieldLabel: "period",
              placeholder: "Enter days",
              inputParentClass: "w-full md:pr-3 pr-2 div_pb-0",
              options: periodOptions,
              nestedInput: { ...register("_period") },
              nestedOnClick: () => {
                if (dirtyFields?._period) {
                  addLookUpOption(
                    {
                      lookup_type_name: "medicine_period",
                      name: watch("_period"),
                    },
                    () => {
                      fetchOptions("medicine_period", setPeriodOptions, showToast);
                    },
                    showToast
                  );
                  setValue("_period", "");
                }
              },
            },
          ],
        },
      ],
    },
  ];

  const onSubmit = async (data) => {
    if (loading) return;

    setLoading(true);
    const {
      _medicine_name,
      _dosage,
      _frequency,
      _period,
      _route,
      _type,
      ...medicalPrescriptionPayload
    } = data;

    const medicalPrescription = {
      ...medicalPrescriptionPayload,
      patient_visit_id: patientVisitId,
      medical_prescription_id: edit ? medicineId : null,
    };

    const endpoint = edit ? API_ENDPOINTS.EDIT_MEDICAL_PRESCRIPTION_DETAILS : API_ENDPOINTS.ADD_MEDICAL_PRESCRIPTION_DETAILS;

    try {
      const response = await commonPostApi(endpoint, medicalPrescription, {
        headers,
      });

      if (response?.data) {
        showToast(
          TOAST_TYPE.SUCCESS,
          edit
            ? FEATURE_MSG.EXAMINATION.PRESCRIPTION_UPDATED
            : FEATURE_MSG.EXAMINATION.PRESCRIPTION_ADDED
        );
        resetAllFields();
        setEdit(false);
        setMedicineId("");
        await init(navigator);
        setLoading(false);
      } else {
        throw new Error(COMMON_MSG.ERROR.NO_DATA_RETURNED);
      }
    } catch (err) {
      console.error("Error saving prescription:", err);
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
      setLoading(false);
    }
  };

  const submitButtons = [
    {
      type: "primarySuccessButton",
      label: edit ? "Update" : "Save",
      class: "md:w-11rem md:pr-3 w-8rem ",
      disabled: Object.keys(dirtyFields).length === 0 || loading,
      onClick: handleSubmit(onSubmit),
    },
    {
      type: "secondaryButton",
      label: "Discard",
      class: "w-7rem",
      onClick: () => {
        resetAllFields();
        setEdit(false);
        setMedicineId("");
      },
    },
  ];

  const init = async (navigator) => {
    setListLoading(true);

    let currentObj = {
      query: {
        searchCriteria: {
          ...navigator?.searchCriteria,
          patient_visit_id: patientVisitId || initialId,
        },
        page: navigator?.page,
        limit: navigator?.rows,
        sortFields: [[navigator?.sortBy, navigator?.sortDir]],
      },
    };

    try {
      const response = await commonPostApi(
        API_ENDPOINTS.SEARCH_MEDICAL_PRESCRIPTION_DETAILS,
        currentObj,
        {
          headers,
        }
      );

      if (response?.data?.data) {
        const finalData = response.data.data;
        setNavigator((prev) => ({
          ...prev,
          totalRecords: response.data.total || 0,
          page: response.data.currentPage || 1,
        }));

        const transformedData = finalData.map(item => {
          const transformed = { ...item };
          medicalFields.forEach(field => {
            if (item[field] && typeof item[field] === 'object') {
              if (item[field].name && item[field].name.trim() !== '') {
                transformed[field] = item[field].name;
              } else if (item[field].value && item[field].value.trim() !== '') {
                transformed[field] = item[field].value;
              } else {
                transformed[field] = '';
              }
            }
          });
          return transformed;
        });

        setProductList(
          orderObj(
            medicalFields,
            generateColumnMetadata({
              data: transformedData,
              nonRequireField: nonRequireMedicalListField,
              alias: { medicine_name: "NAME" },
              defaultColumnsArr: MedicalPrescriptionsColumns,
            })
          )
        );
        setListValues(transformedData);
        setOriginalData(finalData);
      } else {
        setProductList([]);
        setListValues([]);
        setOriginalData([]);
        setNavigator((prev) => ({
          ...prev,
          totalRecords: 0,
          page: 1,
        }));
      }
    } catch (err) {
      console.error("Error fetching prescriptions:", err);
      showToast(TOAST_TYPE.ERROR, FEATURE_MSG.EXAMINATION.PRESCRIPTION_LOAD_FAILED);
      setProductList([]);
      setListValues([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (patientVisitId || initialId) {
      init(navigator);
    }
    // We intentionally depend only on identifiers to avoid refetch loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientVisitId, initialId]);

  useEffect(() => {
    fetchOptions("medicine_name", setMedicineOptions, showToast);
  }, []);

  useEffect(() => {
    fetchOptions("medicine_dosage", setDosageOptions, showToast);
    fetchOptions("medicine_type", setTypeOptions, showToast);
    fetchOptions("medicine_frequency", setFrequencyOptions, showToast);
    fetchOptions("medicine_route", setRouteOptions, showToast);
    fetchOptions("medicine_period", setPeriodOptions, showToast);
  }, []);

  const onRowCountChange = (e) => {
    setListLoading(true);
    const newNavigator = {
      ...navigator,
      rows: e?.target?.value,
      page: 1,
      first: 0,
    };
    setNavigator(newNavigator);
    init(newNavigator);
  };

  function onPageChange(event) {
    setListLoading(true);
    const newNavigator = {
      ...navigator,
      first: event?.first,
      page: event.page + 1,
    };
    setNavigator(newNavigator);
    init(newNavigator);
  }

  const onSortClick = (event) => {
    setLoading(true);
    const sortOrder = event?.sortOrder === 1 ? SORT_ORDER.ASC : SORT_ORDER.DESC;
    setNavigator((prev) => ({
      ...prev,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    }));
    init({
      ...navigator,
      sortBy: event?.sortField,
      sortDir: sortOrder,
      sortFields: [[event?.sortField, sortOrder]],
    });
  };

  function handleSearchChange(value) {
    setListLoading(true);
    const data = convertToSearchField(searchFields, value);
    const newNavigator = {
      ...navigator,
      page: 1,
      first: 0,
      searchCriteria: {
        patient_visit_id: patientVisitId || initialId,
        ...data
      },
    };
    setNavigator(newNavigator);
    init(newNavigator);
  }

  return (
    <div>
      <div className="refraction-container md:p-3 md:pl-4 pl-2">
        <div>
          <div className="public_sans_500 text-secondaryTextColor text-md pb-1 py-3 medical-prescription-header">
            <div>Prescription</div>
          </div>
          {formData?.map((item, index) => {
            return renderInputHeader(
              item,
              index,
              setValue,
              watch,
              control,
              errors
            );
          })}
        </div>
        <div className="ml-2 pt-4">
          <AddButtonSection buttons={submitButtons} />
        </div>
      </div>
      <div className="mt-4 refraction-container">
        <DataGrid
          header={
            <ButtonWithSearch
              isSearchEnabled={true}
              searchPlaceHolder={"Search Prescription"}
              onChange={handleSearchChange}
              searchHeaderLable={"Medical Prescriptions"}
            />
          }
          onRowCountChange={onRowCountChange}
          onPageChange={onPageChange}
          onSortClick={onSortClick}
          navigator={navigator}
          column={productList}
          value={listValues}
          sortable={true}
          loading={listLoading}

          optionsView={[
            {
              items: [
                {
                  label: "Edit",
                  icon: "pi pi-upload",
                  template: itemRenderer,
                },
                {
                  label: "Remove",
                  icon: "pi pi-upload",
                  template: itemRenderer,
                },
              ],
            },
          ]}
        />
        {visibility?.deletePrescription && (
          <PrescriptionDeleteConfirmation
            visible={visibility.deletePrescription}
            medicalPrescription={selectedPrescription}
            setVisible={(bool, final) => {
              setVisibility(prev => ({ ...prev, deletePrescription: bool }));
              if (final) {
                init(navigator);
              }
            }}
          />
        )}
      </div>
    </div>
  );
}
